import React, { useState, useEffect } from 'react';
import { fetchInstagramPosts } from '../../utils/instagramApi';

/**
 * Instagram Feed Component
 * 
 * This component displays Instagram posts from a specified username.
 * It now uses static images from the public directory.
 * 
 * @param {Object} props - Component props
 * @param {string} props.username - Instagram username
 * @param {number} props.count - Number of posts to display
 * @returns {JSX.Element} - Rendered component
 */
const InstagramFeed = ({ username, count = 4 }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        
        // Fetch posts using the username
        const instagramPosts = await fetchInstagramPosts(username, count);
        
        setPosts(instagramPosts);
        setLoading(false);
      } catch (err) {
        console.error('Error loading Instagram posts:', err);
        setError('Failed to load Instagram posts');
        setLoading(false);
      }
    };
    
    loadPosts();
  }, [count, username]);
  
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className="bg-gray-200 aspect-square animate-pulse rounded-lg"></div>
        ))}
      </div>
    );
  }
  
  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {posts.map(post => (
        <a 
          key={post.id} 
          href={post.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md"
        >
          <img 
            src={post.media_url} 
            alt={(post.caption ? post.caption.substring(0, 30) + '...' : 'Instagram post')}
            className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-110"
            onError={(e) => {
              e.target.onerror = null;
              // Use the first static image as fallback
              const imageIndex = parseInt(post.id.split('-')[1]) || 1;
              e.target.src = `/images/instagram/${Math.min(imageIndex, 4)}.jpg`;
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
};

export default InstagramFeed;
